import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import i18n.StringKey
import i18n.tr

/**
 * Web equivalent of iOS `@Environment(\.isReadOnly)` + Android `LocalIsReadOnly`.
 *
 * Maintains a cached subscription status that pages update when they fetch
 * subscription details, and exposes a `gateReadOnly(...)` helper that:
 *   - Returns true (block the action) when in read-only mode, and surfaces a
 *     transient bottom-toast prompting the user to subscribe.
 *   - Returns false (proceed) when the user has write access.
 *
 * The cached value is updated by SubscriptionBanner.updateBanner() (the only
 * place the web app actually fetches `/api/subscribe/status`), so by the time
 * a goal-create or budget-save tries to gate, the cache is fresh.
 */
object ReadOnlyGate {
    // JS is single-threaded — no @Volatile needed.
    private var lastKnownStatus: String? = null

    /**
     * True while a no-account demo session is active. The demo account is
     * read-only end-to-end (the server rejects every write), so we block writes
     * client-side too and nudge the visitor to create an account instead of
     * firing a request that would just 403.
     */
    var demoMode: Boolean = false
        private set

    /** Called by SubscriptionBanner whenever a fresh status is fetched. */
    fun updateStatus(status: String?) {
        lastKnownStatus = status
    }

    /** Enter/leave the no-account demo. */
    fun setDemoMode(on: Boolean) {
        demoMode = on
    }

    /**
     * Read-only only when the subscription isn't active/trialing. The demo is
     * deliberately NOT read-only: the user can do everything, and the ApiService
     * no-ops demo writes so nothing persists (see isDemoSession short-circuits).
     */
    val isReadOnly: Boolean
        get() {
            val s = lastKnownStatus ?: return false
            return s != "active" && s != "trialing"
        }

    /**
     * Gate a write action. Call at the top of every write handler:
     *
     *     if (ReadOnlyGate.block()) return
     *
     * Returns true if blocked (and shows the toast); false otherwise. Demo never
     * blocks — demo writes proceed and are no-oped by the API layer.
     */
    fun block(): Boolean {
        if (!isReadOnly) return false
        showSubscriptionToast()
        return true
    }

    private fun showSubscriptionToast() {
        // Remove any existing toast first so rapid clicks don't stack.
        document.getElementById("wiz3r-readonly-toast")?.remove()

        val toast = document.createElement("div") as HTMLElement
        toast.id = "wiz3r-readonly-toast"
        toast.innerHTML = """
            <div style="display: flex; align-items: center; gap: 12px;">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00d9ff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <span style="color: #fff; font-size: 14px; font-weight: 500;">${StringKey.PAYWALL_BLOCKED_TOAST.tr()}</span>
                <button id="wiz3r-readonly-toast-cta" style="
                    background: #00d9ff; color: #0a0a0a; border: none; border-radius: 6px;
                    padding: 6px 12px; font-size: 13px; font-weight: 600; cursor: pointer;
                ">${StringKey.PAYWALL_BLOCKED_TOAST_CTA.tr()}</button>
            </div>
        """.trimIndent()
        toast.setAttribute("style", """
            position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%) translateY(20px);
            background: #141416; border: 1px solid rgba(0,217,255,0.5); border-radius: 12px;
            padding: 12px 16px; z-index: 99999;
            box-shadow: 0 8px 24px rgba(0,0,0,0.4);
            opacity: 0; transition: opacity 0.25s, transform 0.25s;
        """.trimIndent())
        document.body?.appendChild(toast)

        window.setTimeout({
            toast.style.opacity = "1"
            toast.style.transform = "translateX(-50%) translateY(0)"
        }, 10)

        // Subscribe button → route to the subscribe page (hash-based nav).
        toast.querySelector("#wiz3r-readonly-toast-cta")?.addEventListener("click", {
            window.location.hash = "#subscribe"
            toast.remove()
        })

        // Auto-dismiss after 3 seconds.
        window.setTimeout({
            toast.style.opacity = "0"
            toast.style.transform = "translateX(-50%) translateY(20px)"
            window.setTimeout({ toast.remove() }, 250)
        }, 3000)
    }
}

/** Convenience top-level alias matching the iOS/Android `gateReadOnly()` shape. */
fun gateReadOnly(): Boolean = ReadOnlyGate.block()
